﻿import React, { useState, useEffect, useRef } from "react";
import { api } from "../utils/api.js";
import { getCompany, rememberCompanies, useCompanyInfoVersion } from "../utils/companyInfo.js";

// Type a name or ticker, pick a company. value = ticker (or ""), onChange(ticker).
export default function TickerPicker({ value, onChange, placeholder = "Search any US company", allowClear = false }) {
  useCompanyInfoVersion();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const box = useRef(null);

  useEffect(() => {
    if (!editing) return undefined;
    const close = (e) => {
      if (box.current && !box.current.contains(e.target)) setEditing(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [editing]);

  useEffect(() => {
    const q = text.trim();
    if (!editing || !q) {
      setResults([]);
      return undefined;
    }
    let dead = false;
    setLoading(true);
    const timer = setTimeout(() => {
      api(`/search?q=${encodeURIComponent(q)}&limit=8`)
        .then((rows) => {
          if (dead) return;
          rememberCompanies(rows);
          setResults(rows);
          setLoading(false);
        })
        .catch(() => {
          if (!dead) {
            setResults([]);
            setLoading(false);
          }
        });
    }, 180);
    return () => {
      dead = true;
      clearTimeout(timer);
    };
  }, [text, editing]);

  const company = value ? getCompany(value) : null;
  const label = value ? (company ? `${company.name} (${value})` : value) : "";
  const pick = (r) => {
    onChange(r.ticker);
    setEditing(false);
    setText("");
  };

  return (
    <div ref={box} style={{ position: "relative" }}>
      <input
        className="form-control"
        value={editing ? text : label}
        placeholder={placeholder}
        onFocus={() => {
          setEditing(true);
          setText("");
        }}
        onChange={(e) => setText(e.target.value)}
        autoComplete="off"
      />
      {allowClear && value && !editing && (
        <button type="button" aria-label="Clear" onClick={() => onChange("")}
          style={{ position: "absolute", right: 8, top: 4, border: 0, background: "transparent", fontSize: 20, lineHeight: 1, color: "#888" }}>
          ×
        </button>
      )}
      {editing && text.trim() && (
        <ul style={{ position: "absolute", left: 0, right: 0, top: "100%", zIndex: 1000, margin: "2px 0 0", padding: 0, listStyle: "none",
          background: "#fff", border: "1px solid #ddd", borderRadius: 6, boxShadow: "0 6px 20px rgba(0,0,0,.15)", maxHeight: 280, overflowY: "auto" }}>
          {results.map((r) => (
            <li key={r.ticker}>
              <button type="button" onClick={() => pick(r)}
                style={{ display: "block", width: "100%", textAlign: "left", border: 0, background: "transparent", padding: "8px 12px" }}>
                <strong>{r.name}</strong> <span className="text-muted small">{r.ticker}</span>
                {r.exchange && <span className="text-muted small ms-2">{r.exchange}</span>}
              </button>
            </li>
          ))}
          {loading && <li className="text-muted small p-2">Searching...</li>}
          {!loading && results.length === 0 && <li className="text-muted small p-2">No companies found.</li>}
        </ul>
      )}
    </div>
  );
}
